import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { getAuthToken, setAuthToken, setUserData } from "../utils/auth";
import LoginModal from "../components/landing/LoginModal";
import RegisterModal from "../components/landing/RegisterModal";
import ForgotPasswordModal from "../components/landing/ForgotPasswordModal";
import axios from "axios";
type PaymentMethod = "mbway" | "refmb";

interface Package {
  id: string;
  name: string;
  price: number;
  duration: number;
  description: string;
  features: string[];
}

const PaymentPage = () => {
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user, isLoading } = useAuth();

  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("mbway");
  const [needsVAT, setNeedsVAT] = useState(false);
  const [nif, setNif] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setIsLoadingPackage] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);

  // Show login modal if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setShowLoginModal(true);
    }
  }, [isAuthenticated, isLoading]);

  const handleLoginSuccess = (
    token: string,
    userData: { id: string; email: string; name: string },
  ) => {
    setAuthToken(token);
    setUserData(userData);
    setShowLoginModal(false);
    // Reload to update auth state
    window.location.reload();
  };

  const handleRegisterSuccess = (
    token: string,
    userData: { id: string; email: string; name: string },
  ) => {
    setAuthToken(token);
    setUserData(userData);
    setShowRegisterModal(false);
    // Reload to update auth state
    window.location.reload();
  };

  // Fetch package details
  useEffect(() => {
    const fetchPackage = async () => {
      if (!packageId) {
        setError("ID do pacote não fornecido");
        setIsLoadingPackage(false);
        return;
      }

      try {
        const response = await fetch(`/api/plans/${packageId}`, {});

        if (!response.ok) {
          throw new Error("Erro ao carregar informações do pacote");
        }

        const data = await response.json();
        setSelectedPackage(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Erro ao carregar pacote",
        );
      } finally {
        setIsLoadingPackage(false);
      }
    };

    if (isAuthenticated && packageId) {
      fetchPackage();
    }
  }, [packageId, isAuthenticated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!user || !packageId || !selectedPackage) {
      setError("Informações incompletas para processar o pagamento");
      return;
    }

    if (needsVAT && !nif) {
      setError("Por favor, insira o seu NIF");
      return;
    }

    // Validate NIF format (9 digits) if provided
    if (needsVAT && nif && !/^\d{9}$/.test(nif)) {
      setError("NIF inválido. Deve conter 9 dígitos");
      return;
    }

    // Validate phone number for MB WAY
    if (paymentMethod === "mbway" && !phoneNumber) {
      setError("Por favor, insira o seu número de telefone para MB WAY");
      return;
    }

    if (
      paymentMethod === "mbway" &&
      phoneNumber &&
      !/^\d{9}$/.test(phoneNumber)
    ) {
      setError("Número de telefone inválido. Deve conter 9 dígitos");
      return;
    }

    setIsSubmitting(true);

    try {
      // Handle different payment methods
      if (paymentMethod === "mbway") {
        const options = {
          method: "POST",
          url: `/api/payments/mbway/new?amount=${selectedPackage?.price}&phoneNumber=${phoneNumber}&packageId=${packageId}`,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
        };

        try {
          const response = await axios.request(options);
          if (response.status === 200) {
            navigate(`/payment/checkout?id=${response.data.payment.id}`, {
              state: {
                phrase: "Aceda à sua aplicação MBWay para concluir o pagamento.",
                orderId: response.data.payment.id,
                packageName: selectedPackage.name,
                amount: selectedPackage.price,
                paymentMethod: "MB WAY",
                timestamp: new Date(
                  response.data.payment.createdAt,
                ).toISOString(),
              },
            });
          }
        } catch (error) {
          console.error("MB WAY payment error:", error);
          throw error;
        }
      } else if (paymentMethod === "refmb") {
        const options = {
          method: "POST",
          url: `/api/payments/refmb/new`,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          data: {
            amount: selectedPackage?.price,
            packageId: packageId,
            nif: needsVAT ? nif : undefined,
          },
        };

        try {
          const response = await axios.request(options);
          if (response.status === 200) {
            navigate(`/payment/checkout?id=${response.data.payment.id}`, {
              state: {
                phrase: "Proceda ao pagamento da referência multibanco para concluir a compra.",
                orderId: response.data.payment.id,
                packageName: selectedPackage.name,
                amount: selectedPackage.price,
                paymentMethod: "Multibanco",
                entity: "12514",
                reference: response.data.referenceData,
                timestamp: new Date(
                  response.data.payment.createdAt,
                ).toISOString(),
              },
            });
          }
        } catch (error) {
          console.error("Multibanco reference error:", error);
          throw error;
        }
      } else {
        navigate("/payment/success");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao processar pagamento",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center">
        <div className="text-white text-xl">A carregar...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              Inicie sessão primeiro
            </h1>
            <p className="text-purple-200 text-lg">
              Para continuar com o pagamento, é necessário estar autenticado.
            </p>
            <button
              onClick={() => setShowLoginModal(true)}
              className="mt-4 me-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              Iniciar Sessão
            </button>
            <button
              onClick={() => navigate("/")}
              className="mt-4 px-4 py-2 border-purple-600 border text-white rounded-lg hover:bg-purple-700 transition"
            >
              Voltar à página principal
            </button>
          </div>
        </div>
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => {
            setShowLoginModal(false);
          }}
          onLogin={handleLoginSuccess}
          onSwitchToRegister={() => {
            setShowLoginModal(false);
            setShowRegisterModal(true);
          }}
          onForgotPassword={() => {
            setShowLoginModal(false);
            setShowForgotPasswordModal(true);
          }}
        />
        <RegisterModal
          isOpen={showRegisterModal}
          onClose={() => {
            setShowRegisterModal(false);
          }}
          onRegister={handleRegisterSuccess}
          onSwitchToLogin={() => {
            setShowRegisterModal(false);
            setShowLoginModal(true);
          }}
        />
        <ForgotPasswordModal
          isOpen={showForgotPasswordModal}
          onClose={() => setShowForgotPasswordModal(false)}
          onSuccess={() => setShowForgotPasswordModal(false)}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Pagamento</h1>
          <p className="text-purple-200">
            Complete o seu pagamento de forma segura
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Package Details */}
          <div className=" rounded-2xl p-6 ">
            <h2 className="text-2xl font-bold text-white mb-4">
              Detalhes do Plano
            </h2>

            {selectedPackage ? (
              <div className="space-y-4">
                <div>
                  <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl p-6 border border-purple-400/30">
                    <div className="space-y-1 mb-4">
                      <h3 className="text-2xl font-bold text-white">
                        {selectedPackage.name}
                      </h3>
                      <div className="h-1 w-16 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full"></div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                        <p className="text-sm text-purple-300 uppercase tracking-wide mb-1">
                          Valor
                        </p>
                        <p className="text-4xl font-bold text-white">
                          €{selectedPackage.price.toFixed(2)}
                        </p>
                      </div>

                      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                        <p className="text-sm text-purple-300 uppercase tracking-wide mb-1">
                          Tempo de Chat
                        </p>
                        <p className="text-3xl font-bold text-white">
                          {selectedPackage.duration}{" "}
                          <span className="text-lg text-purple-300">
                            minutos
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedPackage.description && (
                  <p className="text-purple-100">
                    {selectedPackage.description}
                  </p>
                )}

                {selectedPackage.features &&
                  selectedPackage.features.length > 0 && (
                    <div>
                      <h4 className="text-white font-semibold mb-2">
                        Incluído:
                      </h4>
                      <ul className="space-y-2">
                        {selectedPackage.features.map((feature, index) => (
                          <li
                            key={index}
                            className="flex items-start text-purple-100"
                          >
                            <svg
                              className="w-5 h-5 mr-2 text-green-400 flex-shrink-0 mt-0.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            ) : (
              <p className="text-purple-200">Pacote não encontrado</p>
            )}
          </div>

          {/* Payment Form */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <h2 className="text-2xl font-bold text-white mb-6">
              Método de Pagamento
            </h2>

            {error && (
              <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Payment Method Selection */}
              <div>
                <label className="block text-white font-semibold mb-3">
                  Escolha o método de pagamento
                </label>
                <div className="space-y-3">
                  <label
                    className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all transform ${
                      paymentMethod === "mbway"
                        ? "bg-purple-600/30 border-purple-400 shadow-lg scale-[0.98]"
                        : "bg-white/5 border-transparent hover:border-purple-400 hover:bg-white/10"
                    }`}
                  >
                    <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-white/40 bg-white/10 mr-3">
                      {paymentMethod === "mbway" && (
                        <div className="w-3 h-3 rounded-full bg-purple-400 animate-pulse"></div>
                      )}
                    </div>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="mbway"
                      checked={paymentMethod === "mbway"}
                      onChange={(e) =>
                        setPaymentMethod(e.target.value as PaymentMethod)
                      }
                      className="sr-only"
                    />
                    <span className="text-white font-semibold">MB WAY</span>
                  </label>

                  <label
                    className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all transform ${
                      paymentMethod === "refmb"
                        ? "bg-purple-600/30 border-purple-400 shadow-lg scale-[0.98]"
                        : "bg-white/5 border-transparent hover:border-purple-400 hover:bg-white/10"
                    }`}
                  >
                    <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-white/40 bg-white/10 mr-3">
                      {paymentMethod === "refmb" && (
                        <div className="w-3 h-3 rounded-full bg-purple-400 animate-pulse"></div>
                      )}
                    </div>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="refmb"
                      checked={paymentMethod === "refmb"}
                      onChange={(e) =>
                        setPaymentMethod(e.target.value as PaymentMethod)
                      }
                      className="sr-only"
                    />
                    <span className="text-white font-semibold">
                      Referência Multibanco
                    </span>
                  </label>

                </div>
              </div>

              {/* MB WAY Phone Number */}
              {paymentMethod === "mbway" && (
                <div className="bg-purple-500/10 border border-purple-400/30 rounded-lg p-4">
                  <label className="block text-white font-semibold mb-2">
                    Número de Telefone MB WAY
                  </label>
                  <input
                    type="text"
                    value={phoneNumber}
                    onChange={(e) =>
                      setPhoneNumber(
                        e.target.value.replace(/\D/g, "").slice(0, 9),
                      )
                    }
                    placeholder="912345678"
                    maxLength={9}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                  <p className="text-purple-200 text-sm mt-1">
                    Digite o número de 9 dígitos
                  </p>
                </div>
              )}

              {/* VAT Number Section */}
              <div>
                <label className="flex items-center mb-3 cursor-pointer group">
                  <div
                    className={`relative w-6 h-6 rounded border-2 transition-all ${
                      needsVAT
                        ? "bg-purple-600 border-purple-400"
                        : "bg-white/10 border-white/40 group-hover:border-purple-400"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={needsVAT}
                      onChange={(e) => {
                        setNeedsVAT(e.target.checked);
                        if (!e.target.checked) setNif("");
                      }}
                      className="sr-only"
                    />
                    {needsVAT && (
                      <svg
                        className="absolute inset-0 w-full h-full text-white p-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="ml-3 text-white">
                    Preciso de fatura com NIF
                  </span>
                </label>

                {needsVAT && (
                  <div>
                    <label className="block text-white font-semibold mb-2">
                      NIF (Número de Identificação Fiscal)
                    </label>
                    <input
                      type="text"
                      value={nif}
                      onChange={(e) =>
                        setNif(e.target.value.replace(/\D/g, "").slice(0, 9))
                      }
                      placeholder="123456789"
                      maxLength={9}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required={needsVAT}
                    />
                    <p className="text-purple-200 text-sm mt-1">
                      Digite 9 dígitos
                    </p>
                  </div>
                )}
              </div>

              {/* User Info Display */}
              <div className="pt-4 border-t border-white/20">
                <p className="text-purple-200 text-sm">
                  <strong>Utilizador:</strong> {user?.name} ({user?.email})
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || !selectedPackage}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-4 px-6 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {isSubmitting ? "A processar..." : "Confirmar Pagamento"}
              </button>
            </form>
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-8 text-center">
          <p className="text-purple-200 text-sm flex items-center justify-center">
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            Pagamento seguro e encriptado
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
